import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestHelp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    bill_type: "",
    amount_requested: "",
    due_date: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setWarning(null);

    try {
      const response = await axios.post(`${API}/member/help-request`, {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested)
      }, {
        withCredentials: true
      });
      
      if (response.data.warning) {
        setWarning(response.data.warning);
      }
      
      toast.success('Request submitted successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background py-12 px-6">
      <div className="container mx-auto max-w-2xl">
        <Button 
          data-testid="back-to-dashboard-btn"
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <h1 className="text-4xl font-heading font-bold mb-4" data-testid="request-help-title">
            Request Help with a Bill
          </h1>
          <p className="text-muted-foreground mb-8">
            Tell us about the bill you need help with. We'll review your request and get back to you within 24 hours.
          </p>

          {warning && (
            <Alert className="mb-6 border-amber-200 bg-amber-50" data-testid="warning-alert">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                {warning}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="bill_type" data-testid="bill-type-label">Bill Type</Label>
              <Select 
                value={formData.bill_type} 
                onValueChange={(value) => setFormData({...formData, bill_type: value})}
                required
              >
                <SelectTrigger data-testid="bill-type-select" className="input-default mt-2">
                  <SelectValue placeholder="Select bill type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rent">Rent/Mortgage</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="gas">Gas</SelectItem>
                  <SelectItem value="internet">Internet</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="car">Car Payment/Insurance</SelectItem>
                  <SelectItem value="other">Other Essential Bill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount" data-testid="amount-label">Amount Needed</Label>
              <Input
                id="amount"
                data-testid="amount-input"
                type="number"
                step="0.01"
                placeholder="150.00"
                value={formData.amount_requested}
                onChange={(e) => setFormData({...formData, amount_requested: e.target.value})}
                className="input-default mt-2"
                required
              />
            </div>

            <div>
              <Label htmlFor="due_date" data-testid="due-date-label">Bill Due Date</Label>
              <Input
                id="due_date"
                data-testid="due-date-input"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                className="input-default mt-2"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" data-testid="description-label">
                What happened this month?
              </Label>
              <Textarea
                id="description"
                data-testid="description-textarea"
                placeholder="Please explain your situation... (e.g., unexpected car repair, reduced hours at work)"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="input-default mt-2 min-h-[120px]"
                required
              />
            </div>

            <Button
              data-testid="submit-request-btn"
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Your request will be reviewed by our team. We'll notify you of the decision via email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RequestHelp;
